<template>
  <Drawer
    :id="drawerId"
    :title="title"
  >
    <div class="px-4 pt-4">
      <mcs-alert
        v-if="blocked"
        type="error"
        prependIcon="mdi-lock"
        text="Inviting is blocked until you reactivate"
        linkText="Reactivate >"
        :routeUrl="subscriptionRoute"
      />
      <mcs-alert
        v-if="blockMoreUsers"
        type="error"
        prependIcon="mdi-lock"
        text="The max number of users have been invited. Subscribe to add more."
        linkText="Subscribe >"
        :routeUrl="subscriptionRoute"
      >
      </mcs-alert>
      <mcs-alert
        v-if="done && confirmation && confirmation.emailsNotAdded.length"
        :text="`${confirmation.emailsNotAdded.length} people couldn’t be added because they belong to another team`"
        prepend-icon="mdi-account-multiple"
        type="error"
      >
        <template #actions>
          <mcs-btn
            text
            color="black"
            class="px-0"
            @click="openBeacon"
          >
            Contact us
          </mcs-btn>
        </template>
      </mcs-alert>
      <div class="d-flex mb-4 align-center">
        <v-img
          src="https://res.cloudinary.com/mycreativeshop/image/upload/v1731508429/public/invite-person.png"
          max-width="80"
          contain
          class="mr-4"
        />
        <div>
          <div class="subtitle-1">
            {{ message }}
          </div>
          <div class="body-2">
            {{ done ? `We’re sending email invites` : 'Extend creative superpowers to anyone' }}
          </div>
        </div>
        <mcs-chip
          v-if="done"
          class="ml-auto"
          color="successLight"
        >
          Confirmation
        </mcs-chip>
      </div>
      <template v-if="done">
        <mcs-sheet class="greyLight mt-4 body-2">
          <div class="font-weight-bold">
            <v-icon
              class="mr-1"
              small
            >
              mdi-email
            </v-icon>
            Invitation emails
          </div>
          <div>
            An invitation email will be sent in less than 5 minutes. New people will be asked to
            create a password before they login. Once a password is created they will be able to use
            everything you’ve enabled for them.
          </div>
        </mcs-sheet>
        <template v-if="buttons.length">
          <div class="text-center body-1 mt-8 mb-6">
            <hr class="mb-n3" />
            <span class="white px-4">Manage Team</span>
          </div>
          <mcs-btn
            v-for="(button, i) in buttons"
            :key="i"
            color="black"
            large
            class="mt-4"
            block
            :to="button.to"
          >
            {{ button.text }}
          </mcs-btn>
        </template>
      </template>
      <v-form
        v-else
        ref="form"
        v-model="valid"
      >
        <template v-if="!blocked">
          <v-text-field
            v-for="(user, i) in users"
            :key="i"
            v-model="users[i].email"
            :rules="[users.length == 1 ? $rules.required : true, $rules.emailOrEmpty]"
            outlined
            placeholder="Enter email address..."
            label="Email address"
            hide-details="auto"
            class="mb-4"
            @keyup="updateEmailList(i)"
          >
            <template #append>
              <RoleSelect
                v-if="currentCollectionIds.length"
                v-model="users[i].roleId"
              />
            </template>
          </v-text-field>
          <v-select
            v-if="collections.length > 1 && !createCollection && showTeams"
            v-model="currentCollectionIds"
            class="mb-4"
            :items="collections"
            label="Add to team"
            item-text="name"
            item-value="id"
            outlined
            hide-details="auto"
            multiple
            chips
            deletable-chips
          />
        </template>
        <mcs-sheet class="greyLight body-2">
          <div class="font-weight-bold">
            <v-icon
              class="mr-1"
              small
              >mdi-eye</v-icon
            >
            What can they see?
          </div>
          <div>
            Only designs, logos, images, colors and fonts you add to your team area are shared with
            your people. Your billing info, personal designs and uploads are private to you.
          </div>
        </mcs-sheet>
      </v-form>
    </div>
    <Portal
      v-if="!blocked"
      :to="`drawerAppend-${drawerId}`"
    >
      <div
        v-if="!done"
        class="pa-4"
      >
        <v-checkbox
          v-if="showAcknowledged"
          v-model="acknowledged"
          class="mt-0 pt-0"
        >
          <template #label>
            <span class="body-2">
              I acknowledge my subscription will increase to {{ plan.currencySymbol
              }}{{ newPrice }} on
              {{ paymentInfo.date | normalDate }}
              ({{ plan.currencySymbol }}{{ plan.perUserPrice }} additional per person)
            </span>
          </template>
        </v-checkbox>
        <mcs-btn
          large
          color="black"
          block
          :loading="saving"
          :disabled="paymentInfo && !acknowledged && showAcknowledged"
          @click="save"
        >
          Send Invite
        </mcs-btn>
      </div>
    </Portal>
  </Drawer>
</template>

<script>
import Drawer from '@/components/layout/Drawer';
import RoleSelect from '@/components/people/RoleSelect';
import { ANALYTICS_EVENTS, APP_EVENTS, DRAWERS, ENUMS, ROUTES } from '@/utils/constants';
import collections from '@/utils/mixins/collections';
import { uniqBy } from 'lodash';
import { mapGetters } from 'vuex';

export default {
  name: 'AddUsersDrawer',

  components: {
    Drawer,
    RoleSelect
  },

  mixins: [collections],

  data() {
    return {
      drawerId: DRAWERS.ADD_PEOPLE,
      valid: false,
      saving: false,
      currentCollectionIds: [],
      users: [{ email: '', roleId: ENUMS.COLLECTION_ROLES.MEMBER }],
      acknowledged: false,
      done: false,
      confirmation: {},
      paymentInfo: null,
      createCollection: false,
      subscriptionRoute: ROUTES.SUBSCRIPTION.PATH,
      showTeams: false
    };
  },

  computed: {
    showAcknowledged() {
      return this.paymentInfo && parseFloat(this.newPrice) !== parseFloat(this.paymentInfo.price);
    },
    title() {
      return this.done ? 'Complete' : 'Add People';
    },
    blocked() {
      return !this.plan.subscriptionIsCurrent;
    },
    blockMoreUsers() {
      return (
        this.isMCS && !this.plan.isSubscriber && this.free_users_remaining <= this.users.length
      );
    },
    newPrice() {
      if (!this.paymentInfo) {
        return 0;
      }

      let userCount = this.validatedUsers.length - this.free_users_remaining;
      if (userCount < 0) {
        userCount = 0;
      }
      return (this.paymentInfo.price + userCount * this.plan.perUserPrice).toFixed(2);
    },
    validatedUsers() {
      return uniqBy(this.users, 'email').filter((x) => !!x.email);
    },
    free_users_remaining() {
      let val = this.plan.freeTeamMembersAllowed - this.current_users;
      if (val < 0) {
        val = 0;
      }
      return val;
    },
    current_users() {
      if (this.collection) {
        return this.collection.users.length - 1;
      }
      return 0;
    },
    message() {
      var msg = 'Invite your people';
      if (this.done) {
        msg = this.confirmation.emailsAdded.length;
        if (this.confirmation.emailsAdded.length > 1) {
          msg += ' people';
        } else {
          msg += ' person';
        }
        msg += ' added';
      }
      return msg;
    },
    collection() {
      return this.collections.find((x) => x.id === this.currentCollectionIds[0]);
    },
    buttons() {
      if (!this.currentCollectionIds) {
        return [];
      }
      if (this.currentCollectionIds.length > 1) {
        return [];
      }
      return [
        {
          text: 'Add designs',
          to: this.getCollectionsDetailPath(
            ENUMS.ASSET_TYPES.TEMPLATES,
            null,
            this.currentCollectionIds[0]
          )
        },
        {
          text: 'Add logos & images',
          to: this.getCollectionsDetailPath(
            ENUMS.ASSET_TYPES.IMAGES,
            null,
            this.currentCollectionIds[0]
          )
        },
        {
          text: 'Add brand colors',
          to: this.getCollectionsDetailPath(
            ENUMS.ASSET_TYPES.COLORS,
            null,
            this.currentCollectionIds[0]
          )
        }
      ];
    },
    ...mapGetters({
      initialData: 'drawers/data',
      top_drawer: 'drawers/topDrawer',
      drawerOpenedAt: 'drawers/lastOpenedAt',
      collections: 'admin/collections/list',
      profile: 'user/profile',
      plan: 'user/plan',
      master_account: 'user/master_account',
      isMCS: 'user/isMCS'
    })
  },

  watch: {
    async drawerOpenedAt() {
      if (this.drawerId === this.top_drawer?.id) {
        this.$mixpanel.trackEvent(
          ANALYTICS_EVENTS.TEAMS.NAME,
          ANALYTICS_EVENTS.TEAMS.ACTIONS.INVITE_DRAWER_OPENED
        );
        await this.$store.dispatch('admin/collections/getList');
        this.confirmation = { emailsAdded: [], emailsNotAdded: [] };
        const initial = this.initialData(this.drawerId) || {};
        this.currentCollectionIds = [
          initial.collectionId || (this.collections.length === 1 ? this.collections[0].id : null)
        ].filter((x) => !!x);
        this.createCollection = initial.createCollection || false;
        if (this.createCollection) {
          this.currentCollectionIds = [];
        }
        if (this.blockMoreUsers) {
          this.users = [];
        } else {
          this.users = [{ email: '', roleId: ENUMS.COLLECTION_ROLES.MEMBER }];
        }
        this.done = false;
        this.acknowledged = false;
        this.showTeams = !initial.collectionId;
        this.loadPaymentInfo();
      }
    }
  },

  methods: {
    loadPaymentInfo() {
      this.paymentInfo = null;
      if (this.master_account.externalBilling) {
        return;
      }
      this.$api
        .get(
          `user/getNextPayment?collectionId=${
            this.currentCollectionIds.length === 1 ? this.currentCollectionIds[0] : null
          }`
        )
        .then((data) => {
          this.paymentInfo = data.data;
        })
        .catch((error) => {
          this.logError(error);
        });
    },
    openBeacon() {
      this.$eventBus.$emit(APP_EVENTS.OPEN_HELPSCOUT, { ask: true });
    },
    cancel() {
      this.$store.commit('drawers/CLOSE_ALL');
    },
    save() {
      const users = this.validatedUsers;
      if (this.$refs.form.validate() && users.length) {
        this.saving = true;
        this.$store
          .dispatch('user/bulkAddUsers', {
            users: users,
            collectionIds: this.currentCollectionIds,
            createCollection: this.createCollection
          })
          .then((data) => {
            if (this.$refs.form) {
              this.$refs.form.reset();
            }
            if (this.createCollection) {
              this.$store.dispatch('config/getAppData').then(() => {
                this.$store.dispatch('subscription/getDetail');
              });
            }
            this.$store.dispatch('admin/collections/getList');
            this.$eventBus.$emit(APP_EVENTS.REFRESH_PEOPLE);
            this.confirmation = data;
            this.currentCollectionIds = data.collectionIds;
            this.done = true;
            if (this.confirmation.emailsNotAdded.length) {
              this.$mixpanel.trackEvent(
                ANALYTICS_EVENTS.TEAMS.NAME,
                ANALYTICS_EVENTS.TEAMS.ACTIONS.INVITE_ERROR,
                { message: `emails not added: ${this.confirmation.emailsNotAdded.join(', ')}` }
              );
            }
          })
          .catch((error) => {
            this.logError(error, 'An error occurred while inviting people.');
            this.$mixpanel.trackEvent(
              ANALYTICS_EVENTS.TEAMS.NAME,
              ANALYTICS_EVENTS.TEAMS.ACTIONS.INVITE_ERROR,
              { message: error.message }
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    updateEmailList(index) {
      const current = this.users[index];
      if (current.email.length > 0) {
        // add empty if one doesn't already exist
        let found = false;
        for (let i = 0; i < this.users.length; i++) {
          if (!this.users[i].email.length) {
            found = true;
          }
        }
        // add logic for not subscribed and free users remaining

        if (this.blockMoreUsers) {
          return;
        }

        if (!found) {
          this.users.push({ email: '', roleId: ENUMS.COLLECTION_ROLES.MEMBER });
          this.acknowledged = false;
        }
      } else {
        // remove all non-empty
        for (let i = 0; i < this.users.length; i++) {
          if (i !== index && !this.users[i].email.length) {
            this.users.splice(i, 1);
            this.acknowledged = false;
            this.showTooManyUsersError = false;
            break;
          }
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .v-input__append-inner {
  margin-top: auto !important;
  margin-bottom: auto !important;
}
</style>
